// Decisions behind the Admin Console's headline numbers, kept out of the page so
// they can be tested.

/**
 * Is this drone online? A live telemetry entry answers first. No entry does not
 * mean offline: the socket carries another operator's drone only under an
 * anonymous daily handle, so the server's own check (isLive on /admin/drones) is
 * the answer for those.
 */
export function droneOnline(drone, telemetry = {}) {
  if (!drone) return false;
  const live = telemetry[drone.droneId] || telemetry[drone.id] || (drone._id ? telemetry[String(drone._id)] : null);
  if (live) return live.connected !== false && !live.stale;
  return drone.isLive !== undefined ? !!drone.isLive : drone.status === 'connected';
}

const NEAR_LIMIT = 0.9;

/**
 * What an admin should look at now, worst first. Each item says what is wrong and
 * which section deals with it (`go`). Only things that need a person: a parcel that
 * failed last week is history, a drone off route now is not.
 */
export function attentionItems({ intents = [], fleet = [], stats = null, database = null }) {
  const nameOf = (id) => (fleet.find((d) => d.droneId === id) || {}).name || id;
  const items = [];
  const push = (level, key, title, detail, go) => items.push({ level, key, title, detail, go });

  if (database === 'down') push('red', 'database', 'Database is down', 'Nothing can be saved or reserved until it is back.', 'system');

  const flight = (intent, what, fallback) => {
    const who = intent.owner && intent.owner.name;
    return { title: `${nameOf(intent.droneId)}: ${what}`, detail: [intent.stateReason || fallback, who].filter(Boolean).join(' · ') };
  };
  intents.filter((i) => i.state === 'Contingent').forEach((i) => {
    const f = flight(i, 'contingency', 'Link loss, low battery or abort');
    push('red', i.intentId, f.title, f.detail, 'airspace');
  });
  intents.filter((i) => i.state === 'Nonconforming').forEach((i) => {
    const f = flight(i, 'off route', 'Outside its reserved volume');
    push('amber', i.intentId, f.title, f.detail, 'airspace');
  });

  const requested = (stats && stats.deliveriesByStatus && stats.deliveriesByStatus.requested) || 0;
  if (requested > 0) {
    push('amber', 'requests', `${requested} customer request${requested > 1 ? 's' : ''} waiting`, 'An operator has to approve before it can fly.', 'deliveries');
  }

  const limits = (stats && stats.limits) || {};
  [['Members', stats && stats.totalUsers, limits.maxUsers], ['Drones', stats && stats.totalDrones, limits.maxDrones]].forEach(([label, value, max]) => {
    if (max && value >= max * NEAR_LIMIT) push('amber', `limit-${label}`, `${label}: ${value} of ${max}`, 'Close to the plan limit.', 'system');
  });
  return items;
}

/**
 * Who can be handed a drone: accounts someone can still log in to, you first, then
 * other admins, then operators. Never its current owner.
 */
export function moveTargets(users = [], selfId, exceptId) {
  const rank = (u) => (u.id === selfId ? 0 : u.role === 'admin' ? 1 : 2);
  return users
    .filter((u) => !u.suspended && u.id !== exceptId)
    .sort((a, b) => rank(a) - rank(b) || String(a.name).localeCompare(String(b.name)));
}

/** "1 super admin, 1 admin, 5 operators" */
export function memberSummary(stats) {
  const roles = (stats && stats.usersByRole) || {};
  const operators = roles.operator || 0;
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const parts = [];
  if (stats && stats.superAdmins !== undefined) {
    parts.push(plural(stats.superAdmins, 'super admin'));
    const others = (roles.admin || 0) - stats.superAdmins;
    if (others > 0) parts.push(plural(others, 'admin'));
  } else {
    parts.push(plural(roles.admin || 0, 'admin'));
  }
  parts.push(plural(operators, 'operator'));
  return parts.join(', ');
}

const memberRank = (m) => {
  if (m.suspended) return 3;
  if (m.superAdmin) return 0;
  return m.role === 'admin' ? 1 : 2;
};

/** Who runs the airspace first, suspended accounts last, by name within each. */
export function sortMembers(members) {
  return [...members].sort((a, b) => memberRank(a) - memberRank(b) || String(a.name).localeCompare(String(b.name)));
}

/** The Members filter. Operators and admins include suspended ones; that filter finds them. */
export function memberMatches(member, filter) {
  if (filter === 'suspended') return !!member.suspended;
  return filter === 'all' || member.role === filter;
}

const RESERVATION_ORDER = { Contingent: 0, Nonconforming: 1, Activated: 2, Accepted: 3 };

/** Trouble first, then what is flying, then what has not left. Soonest to end first within each. */
export function sortReservations(intents) {
  const rank = (i) => (RESERVATION_ORDER[i.state] ?? 4);
  return [...intents].sort((a, b) => rank(a) - rank(b) || new Date(a.timeEnd) - new Date(b.timeEnd));
}

const minutes = (ms) => Math.max(1, Math.round(ms / 60000));

/** "Starts in 4 min", "14 min left" or "Overdue 3 min", worked out from the booked window. */
export function reservationClock(intent, now = Date.now()) {
  const start = new Date(intent.timeStart).getTime();
  const end = new Date(intent.timeEnd).getTime();
  if (now >= end) return { text: `Overdue ${minutes(now - end)} min`, overdue: true };
  if (now < start) return { text: `Starts in ${minutes(start - now)} min`, overdue: false };
  return { text: `${minutes(end - now)} min left`, overdue: false };
}

const AIRBORNE_STATES = new Set(['Activated', 'Nonconforming', 'Contingent']);

/**
 * What releasing this reservation would do. It only ends the booking: nothing is
 * sent to the drone, so a drone still up keeps flying with its airspace open to
 * others. Released anyway when the admin knows better, e.g. a lost link after a
 * landing, which still reads as flying here.
 *   flying  — armed and reporting
 *   unknown — booked as airborne, not reporting: it may be down, it may not
 *   ground  — not armed, or never left
 */
export function releaseRisk(intent, positions = []) {
  const position = positions.find((p) => p.droneId === intent.droneId);
  const reporting = !!position && position.isLive !== false;
  if (reporting && position.armed) return 'flying';
  if (AIRBORNE_STATES.has(intent.state) && !reporting) return 'unknown';
  return 'ground';
}

/** How a drone reaches UTMS, in words. The raw connection string stays as a tooltip. */
export function droneLink(connectionString, isSimulated = false) {
  const s = String(connectionString || '').trim().toLowerCase();
  if (s.startsWith('mock')) return 'Simulator';
  if (s === 'mqtt') return '4G onboard';
  if (/^(com\d|\/dev\/)/.test(s)) return 'USB / radio';
  if (s.startsWith('udp')) return 'UDP';
  if (s.startsWith('tcp')) return 'TCP';
  if (isSimulated) return 'Simulator';
  return s ? connectionString : 'Not set';
}

/** Armed first, then online, then offline, by name within each. */
export function sortFleet(drones, isOnline) {
  const rank = (d) => {
    if (!isOnline(d)) return 2;
    return d.telemetry && d.telemetry.armed ? 0 : 1;
  };
  return [...drones].sort((a, b) => rank(a) - rank(b) || String(a.name).localeCompare(String(b.name)));
}

/** The operators in a list, for its filter. */
export function operatorsOf(list) {
  return [...new Set(list.map((x) => x.owner && x.owner.name).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

// Most urgent first; this is also the order of the filter tabs.
export const DELIVERY_STATUS_ORDER = ['requested', 'in_flight', 'landed', 'assigned', 'pending', 'failed', 'delivered', 'cancelled'];
const DELIVERY_STATUS_TEXT = {
  requested: 'Waiting approval', pending: 'Queued', assigned: 'Assigned', in_flight: 'In flight', landed: 'Landed (medical)',
  delivered: 'Delivered', failed: 'Failed', cancelled: 'Cancelled',
};
export const deliveryStatusText = (status) => DELIVERY_STATUS_TEXT[status] || String(status || 'Unknown').replace(/_/g, ' ');

/** What still needs something first, newest first within each status. */
export function sortDeliveries(jobs) {
  const rank = (j) => {
    const i = DELIVERY_STATUS_ORDER.indexOf(j.status);
    return i < 0 ? DELIVERY_STATUS_ORDER.length : i;
  };
  return [...jobs].sort((a, b) => rank(a) - rank(b) || new Date(b.createdAt) - new Date(a.createdAt));
}

const SERIOUS_ACTION = /force_disarm|delete|override/;
export const auditSerious = (action) => SERIOUS_ACTION.test(action);

export function auditCategory(action) {
  const a = String(action);
  if (a.startsWith('mission.') || a.startsWith('airspace.')) return 'flights';
  if (a.startsWith('drone.')) return 'drones';
  if (a.startsWith('user.')) return 'members';
  return 'settings';
}

export function auditMatches(action, filter) {
  if (filter === 'all') return true;
  return filter === 'serious' ? auditSerious(action) : auditCategory(action) === filter;
}

/** The note on an entry, without the ids the Target column already says. */
export function auditDetail(detail) {
  const text = String(detail || '').replace(/\b(drone|intent)=\S+/g, '').replace(/\s+/g, ' ').trim();
  return text || null;
}

/**
 * What an entry acted on, readable: a drone's name, or a short id. A member's entry
 * says who in its note, and its id means nothing to a person.
 */
export function auditTarget(log, nameOf) {
  if (!log.resourceId || String(log.action).startsWith('user.')) return null;
  const id = String(log.resourceId);
  const name = nameOf(id);
  if (name && name !== id) return name;
  return id.length > 14 ? `${id.slice(0, 12)}…` : id;
}

/** "Today", "Yesterday" or a date, on the viewer's clock. */
export function auditDay(at, now = Date.now()) {
  const day = (t) => { const d = new Date(t); return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime(); };
  const days = Math.round((day(now) - day(at)) / 86400000);
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return new Date(at).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

export const fmtLatency = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`);
